import {
    fetchMediaList as _fetchMediaList,
    fetchPublicMediaList as _fetchPublicMediaList,
    uploadMedia as _uploadMedia,
    deleteMedia as _deleteMedia,
    updateMediaMeta as _updateMediaMeta,
    getDirectUploadUrl as _getDirectUploadUrl,
    completeDirectUpload as _completeDirectUpload
} from "../../../utils/api";

// エラータイプの定義
export const UPLOAD_ERROR_TYPES = {
    SIGNATURE_EXPIRED: 'SIGNATURE_EXPIRED',
    CORS_ERROR: 'CORS_ERROR',
    NETWORK_ERROR: 'NETWORK_ERROR',
    VERIFICATION_FAILED: 'VERIFICATION_FAILED',
    SIZE_ERROR: 'SIZE_ERROR',
    UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

// エラー分類関数
export const classifyUploadError = (error) => {
    // ネットワークエラー
    if (!error.response && !error.request) {
        return UPLOAD_ERROR_TYPES.NETWORK_ERROR;
    }

    // CORSエラー
    if (error.message && (
        error.message.includes('CORS') ||
        error.message.includes('Access-Control') ||
        error.message.includes('Cross-Origin')
    )) {
        return UPLOAD_ERROR_TYPES.CORS_ERROR;
    }

    // S3アップロードエラー（署名切れの可能性）
    if (error.message && error.message.includes('S3 upload failed')) {
        const status = error.status || error.response?.status;
        if (status === 403 || status === 401) {
            return UPLOAD_ERROR_TYPES.SIGNATURE_EXPIRED;
        }
        return UPLOAD_ERROR_TYPES.NETWORK_ERROR;
    }

    // バックエンドレスポンスエラー
    if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;

        // 検証失敗（400エラーでファイルが見つからない等）
        if (status === 400) {
            if (errorData?.error && (
                errorData.error.includes('見つかりません') ||
                errorData.error.includes('検証') ||
                errorData.error.includes('アップロードされたファイル')
            )) {
                return UPLOAD_ERROR_TYPES.VERIFICATION_FAILED;
            }
        }

        // ファイルサイズエラー
        if (status === 413 || (errorData?.error && errorData.error.includes('サイズ'))) {
            return UPLOAD_ERROR_TYPES.SIZE_ERROR;
        }

        // 署名切れ（403エラー）
        if (status === 403) {
            return UPLOAD_ERROR_TYPES.SIGNATURE_EXPIRED;
        }
    }

    return UPLOAD_ERROR_TYPES.UNKNOWN_ERROR;
};

// エラーメッセージ取得関数
export const getErrorMessage = (errorType, error) => {
    const errorMessages = {
        [UPLOAD_ERROR_TYPES.SIGNATURE_EXPIRED]: '署名URLの有効期限が切れました。再度お試しください。',
        [UPLOAD_ERROR_TYPES.CORS_ERROR]: 'CORSエラーが発生しました。管理者にお問い合わせください。',
        [UPLOAD_ERROR_TYPES.NETWORK_ERROR]: 'ネットワークエラーが発生しました。接続を確認して再度お試しください。',
        [UPLOAD_ERROR_TYPES.VERIFICATION_FAILED]: error?.response?.data?.message || error?.response?.data?.error || 'ファイルの検証に失敗しました。',
        [UPLOAD_ERROR_TYPES.SIZE_ERROR]: 'ファイルサイズが大きすぎます（動画：50MB以下、画像：10MB以下、音声：20MB以下）。',
        [UPLOAD_ERROR_TYPES.UNKNOWN_ERROR]: error?.response?.data?.message || error?.response?.data?.error || error?.message || 'アップロードに失敗しました。'
    };

    return errorMessages[errorType] || errorMessages[UPLOAD_ERROR_TYPES.UNKNOWN_ERROR];
};

export const fetchMediaList = (locationId) =>
    _fetchMediaList(locationId);

export const fetchPublicMediaList = (publicUrl, locationId) =>
    _fetchPublicMediaList(publicUrl, locationId);

export const uploadMedia = async (file, locationId, name, description, mediaType) => {
    try {
        const result = await _uploadMedia(file, locationId, name, description, mediaType);
        return result;
    } catch (error) {
        console.error("Media upload failed:", error);

        let message = "アップロードに失敗しました。";

        if (error?.response?.status === 400) {
            // バックエンドからの詳細なエラーメッセージを取得
            if (error?.response?.data?.message || error?.response?.data?.error) {
                message = error.response.data.message || error.response.data.error;
            } else if (error?.response?.data?.detail) {
                message = error.response.data.detail;
            } else {
                message = "無効なファイル形式または必須項目が不足しています。";
            }
        } else if (error?.response?.status === 413) {
            message = "ファイルサイズが大きすぎます（動画：50MB以下、画像：10MB以下、音声：20MB以下）。";
        } else if (error?.response?.data?.detail) {
            message = error.response.data.detail;
        }

        throw new Error(message);
    }
};

export const deleteMedia = (mediaId) =>
    _deleteMedia(mediaId);

export const updateMediaMeta = (mediaId, { name, description }) =>
    _updateMediaMeta(mediaId, { name, description });

export const uploadMediaDirect = async (file, locationId, name, description, mediaType) => {
    try {
        // 1. 署名付きURLを取得
        const presignedData = await _getDirectUploadUrl(
            locationId,
            file.name,
            file.type,
            file.size
        );

        // 2. ファイルをS3に直接アップロード
        const uploadResponse = await fetch(presignedData.presigned_url, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': file.type,
            },
        });

        if (!uploadResponse.ok) {
            const error = new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
            error.status = uploadResponse.status;
            throw error;
        }

        // 3. アップロード完了をバックエンドに通知（session_idを含める）
        const result = await _completeDirectUpload(
            locationId,
            presignedData.upload_path,
            name || file.name,
            description || "",
            mediaType || "image",
            presignedData.session_id  // session_idを追加
        );

        return result;
    } catch (error) {
        console.error("Direct media upload failed:", error);

        // エラータイプを分類
        const errorType = classifyUploadError(error);
        const message = getErrorMessage(errorType, error);

        // エラーオブジェクトにタイプを追加
        const classifiedError = new Error(message);
        classifiedError.errorType = errorType;
        classifiedError.originalError = error;

        throw classifiedError;
    }
};
